<template>
	<div class="footers">
		<div class="logo-bar">
			<div class="row">
				<div class="columns column3">
					<h4>{{ defaults[locale].website.hotelName }} {{ $t('isPartOf') }}</h4>
					<a href="https://www.finehotelsandsuites.com/" target="_blank">
						<img
							src="~/assets/images/finehotels/logo-fine-hotels-suites.svg"
							class="footer-logo"
							style="width: 100%; max-width: 120px"
							loading="lazy"
							alt="Fine Hotels & Suites"
						/>
					</a>
				</div>
				<div class="columns column9">
					<h4>{{ $t('otherHotels') }}</h4>
					<a href="https://www.casajulia.nl/" target="_blank">
						<img
							src="~/assets/images/finehotels/logo-casa-julia.svg"
							class="footer-logo"
							style="width: 100%; max-width: 154px"
							loading="lazy"
							alt="Casa Julia"
						/>
					</a>
					<a href="https://www.stadsvillamout.nl/" target="_blank">
						<img
							src="~/assets/images/finehotels/logo-stadsvilla-mout.svg"
							class="footer-logo"
							style="width: 100%; max-width: 198px"
							loading="lazy"
							alt="Stadsvilla Mout"
						/>
					</a>
					<a href="https://www.mozaic.nl/" target="_blank">
						<img
							src="~/assets/images/finehotels/logo-stadsvilla-mozaic.svg"
							class="footer-logo"
							style="width: 100%; max-width: 198px"
							loading="lazy"
							alt="Stadsvilla Mozaic"
						/>
					</a>
				</div>
				<div class="columns column12">
					<div class="border" />
				</div>
			</div>
		</div>
		<footer class="main-footer">
			<div class="row">
				<div class="columns column4">
					<h4>{{ defaults[locale].website.hotelName }}</h4>
					<p class="address">
						<span>{{ defaults[locale].website.street }} {{ defaults[locale].website.number }}</span>
						<span>{{ defaults[locale].website.postalCode }} {{ defaults[locale].website.city }}</span>
						<span>{{ defaults[locale].website.country }}</span>
					</p>
				</div>
				<div class="columns column4">
					<h4>{{ $t('contact') }}</h4>
					<p>
						<a :href="`tel:${$phoneRaw(defaults[locale].website.phone)}`">
							<span>{{ defaults[locale].website.phone }}</span>
						</a>
						<a :href="`mailto:${defaults[locale].website.email}`">
							<span>{{ defaults[locale].website.email }}</span>
						</a>
					</p>
					<social-media :socials="socials" />
				</div>
				<div class="columns column4">
					<h4>{{ $t('quickLinks') }}</h4>
					<p>
						<nuxt-link v-for="item in footermenu[locale]" :key="item.filename" :to="item.filename">
							{{ item.header }}
						</nuxt-link>
						<a v-if="defaults[locale].cookieConsent.published" href="#" @click.prevent="openConsent"
							>{{ $t('consentOpen') }}
						</a>
					</p>
				</div>
			</div>
		</footer>
		<footer class="author-footer">
			<div class="row">
				<div class="columns column12">
					<div class="border" />
					<div class="bottomfooter-row">
						<p class="align-left">
							<a
								href="https://www.becurious.com"
								target="_blank"
								rel="noopener noreferrer"
								title="Hotel website design by Becurious"
							>
								Hotel Website Design by Becurious</a
							>
						</p>
						<div class="menu align-right">
							<span v-for="(item, idx) in bottomfooter[locale]" :key="item.filename">
								<nuxt-link :to="item.filename">
									{{ item.header }}
								</nuxt-link>
								<span v-if="idx < bottomfooter[locale].length - 1"> | </span>
							</span>
						</div>
					</div>
				</div>
			</div>
		</footer>
	</div>
</template>

<script setup>
const { data: footer } = await useWebsiteFetch('footermenu');
const { footermenu, bottomfooter } = footer.value;
const { locale } = useI18n();
const { openConsent } = useCookieConsent();

const { defaults, socials, fetchDefaults } = useDefaults();

await fetchDefaults();
</script>

<style lang="scss" scoped>
.footers {
	color: var(--footer-color);
	background: var(--footer-background-color);

	a,
	span {
		color: var(--footer-color);
		text-decoration: none;
	}

	h3,
	h4 {
		color: var(--footer-color);
		letter-spacing: 1px;
	}

	h4 {
		font-family: var(--body-font-family);
		font-size: var(--body-font-size);
		font-weight: 600;
	}
}

.logo-bar {
	margin: 75px 0 0;

	.columns {
		padding: 20px;
	}

	.column3 {
		border-right: 1px solid #000;

		a {
			width: 100%;
		}
	}

	.column9 {
		display: flex;
		flex-flow: row wrap;
		padding-left: 40px;

		h4 {
			width: 100%;
		}

		a {
			width: 33%;
		}
	}

	a {
		margin: 20px 0 0;
		display: inline-block;
	}

	.border {
		margin: 30px 0 0;
	}
}

.main-footer {
	padding: 50px 0;

	p {
		font-size: 0.9em;
	}

	a,
	span {
		display: block;
	}

	a:hover {
		text-decoration: underline;
	}
}

.bottomfooter-row {
	display: flex;
	align-items: center;
	width: 100%;
	text-align: center;

	.menu {
		margin-left: auto;
	}
}

.author-footer {
	font-size: 12px;
	padding: 0;
	text-align: center;
	width: 100%;

	img {
		max-width: 20px;
		aspect-ratio: 2 / 3;
		margin: 0 1px 0 2px;
	}
}

.border {
	background: #333;
	width: 100%;
	height: 1px;
}

@media (max-width: 1080px) {
	footer .columns {
		margin: 15px auto;
	}

	.logo-bar {
		margin: 50px auto;

		.column3 {
			width: calc(100% - 40px);
			margin: 0 auto 25px;
			padding: 0 20px 50px;
			border-right: 0;
			border-bottom: 1px solid #000;
			text-align: center;
		}
	}
}

@media (max-width: 580px) {
	footer {
		text-align: center;
	}

	.logo-bar {
		.column9 {
			justify-content: space-between;

			a {
				width: 25%;

				&:nth-child(2) {
					img {
						max-width: 70px !important;
					}
				}
			}
		}
	}

	.main-footer {
		padding: 75px 0 50px;
	}

	.bottomfooter-row {
		flex-flow: column-reverse wrap;
		align-items: center;

		p,
		.menu {
			margin: 5px auto;
			text-align: center;
		}
	}
}

@media (max-width: 420px) {
	.logo-bar {
		.column9 {
			a {
				&:nth-child(2) {
					img {
						max-width: 60px !important;
					}
				}
			}
		}
	}
}
</style>
